import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ShieldCheck } from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";

const COPY = {
  buy: { title: "Buy Properties & Land", listing_type: "SELL", path: "/buy" },
  rent: { title: "Rent Homes & Commercial Spaces", listing_type: "RENT", path: "/rent" },
  commercial: { title: "Commercial Land & Properties", listing_type: "SELL", category: "COMMERCIAL", path: "/commercial" },
  pg: { title: "PG / Hostel Discovery", listing_type: "RENT", category: "PG_HOSTEL", path: "/pg-hostel" },
  plot: { title: "Plot / Land First Portal", listing_type: "SELL", property_types: "LAND,PLOT", path: "/plot-land" },
};

export default function CategoryLanding({ type = "plot" }) {
  const cfg = COPY[type] || COPY.plot;
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const query = useMemo(() => ({
    city: city || undefined,
    category: cfg.category || undefined,
    property_types: cfg.property_types || undefined,
    listing_type: cfg.listing_type,
    limit: 12,
  }), [city, cfg.category, cfg.property_types, cfg.listing_type]);
  const searchHref = useMemo(() => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (cfg.category) params.set("category", cfg.category);
    if (cfg.property_types) params.set("property_types", cfg.property_types);
    params.set("listing_type", cfg.listing_type);
    return `/properties?${params.toString()}`;
  }, [city, cfg.category, cfg.property_types, cfg.listing_type]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    api.get("/v1/search/properties", { params: query })
      .then(({ data }) => { if (live) setItems(asItems(data)); })
      .catch((requestError) => {
        if (live) {
          setItems([]);
          setError(formatApiError(requestError) || "Listings could not be loaded.");
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [query, reloadKey]);

  return (
    <div className="min-h-screen bg-background" data-testid={`category-${type}-page`}>
      <SEO title={`${cfg.title} | ShreeHomes`} description="Search published listings on ShreeHomes — The real place for real estate." />
      <section className="bg-primary text-primary-foreground py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground/70 mb-3">Verified real estate discovery</p>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-tight">{cfg.title}</h1>
            <p className="mt-5 text-primary-foreground/75 max-w-xl">Built for land and plot discovery with visible trust signals, privacy-controlled contacts, and tracked enquiries.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City or locality" className="bg-background text-foreground" />
              <Button asChild variant="secondary" className="font-bold"><Link to={searchHref}><Search className="w-4 h-4 mr-2"/>Search</Link></Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {["Verified Owners", "Verified Documents", "Plots on Demand"].map((x) => (
              <div key={x} className="rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 p-5"><ShieldCheck className="w-6 h-6 mb-3"/><p className="font-bold">{x}</p></div>
            ))}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex items-end justify-between mb-6">
          <div><p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Live results</p><h2 className="font-display text-3xl font-black tracking-tight">{city ? `Listings in ${city}` : "Latest listings"}</h2></div>
          <Button asChild variant="outline"><Link to="/plot-on-demand"><MapPin className="w-4 h-4 mr-2"/>Open Plots on Demand</Link></Button>
        </div>
        {loading ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Loading listings">{[0, 1, 2].map((item) => <div key={item} className="h-96 rounded-2xl shimmer" />)}</div> : error ? (
          <div className="border border-destructive/30 bg-destructive/5 rounded-xl py-14 px-6 text-center" role="alert">
            <p className="font-bold text-foreground">Listings could not be loaded</p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button type="button" className="mt-5" onClick={() => setReloadKey((key) => key + 1)}>Try again</Button>
          </div>
        ) : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((p) => <PropertyCard key={p.property_id || p.id} property={p} />)}</div>}
        {!loading && !error && items.length === 0 && <div className="border border-dashed rounded-xl py-16 text-center text-muted-foreground">No listings yet. Try another city or submit a request through Plots on Demand.</div>}
      </section>
    </div>
  );
}
